if (!customElements.get("m-slideshow")) {
  class MSlideshow extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.autoplay = this.dataset.autoplay === "true";
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed);
      this.showArrows = this.dataset.enableArrows === "true";
      this.showDots = this.dataset.enableDots === "true";
      this.adaptHeight = this.dataset.slideHeight === "adapt";
      this.id = this.dataset.id;
      this.sliderContainer = this.querySelector(".swiper-container");
      // Loop mode clones every slide. With a single video slide that meant three
      // copies of the video decoding at once, and the visible one could freeze.
      this.multiSlide = this.querySelectorAll(".swiper-slide").length > 1;

      this.lastVideo = null;
      this.lastActive = null;
      this.timeout = null;
      this.initSlider();
    }

    initSlider() {
      this.slider = new MinimogLibs.Swiper(this.sliderContainer, {
        init: false,
        autoplay: this.autoplay
          ? {
              delay: this.autoplaySpeed * 1000,
              disableOnInteraction: true,
            }
          : false,
        slidesPerView: 1,
        slidesPerGroup: 1,
        loop: this.multiSlide,
        allowTouchMove: this.multiSlide,
        effect: "fade",
        fadeEffect: {
          crossFade: true,
        },
        speed: 1000,
        navigation: this.showArrows
          ? {
              nextEl: this.querySelector(".m-slider-controls__button-next"),
              prevEl: this.querySelector(".m-slider-controls__button-prev"),
            }
          : false,
        pagination: this.showDots
          ? {
              el: this.querySelector(".swiper-pagination"),
              clickable: true,
              bulletClass: "m-dot",
              bulletActiveClass: "m-dot--active",
            }
          : false,
        breakpoints: {
          992: {
            threshold: 2,
          },
        },
        on: {
          init() {
            setTimeout(() => {
              window.dispatchEvent(new Event("resize"));
            }, 100);
          },
        },
      });

      this.lastSlide = null;
      this.currentSlide = null;
      this.slider && this.slider.on("init", this.handleChange.bind(this));
      this.slider && this.slider.on("slideChange", this.handleChange.bind(this));
      this.slider.init();

      if (Shopify.designMode) {
        document.addEventListener("shopify:block:select", (e) => this.onBlockSelect(e));
      }
    }

    handleChange(swiper) {
      const { activeIndex, slides } = swiper;
      const slideType = slides[activeIndex] && slides[activeIndex].dataset.slideType;
      this.lastActive && this.slider.slideToLoop(this.lastActive);
      if (slideType === "video_slide") {
        const video = slides[activeIndex] && slides[activeIndex].querySelector("video");

        // Sliders below the first section start their video only once it nears the screen.
        if (video && video.dataset.ojLazy && !video.dataset.ojNear) {
          this.watchLazyVideo(video);
          return;
        }

        if (video) {
          let playPromise = video.play();
          // Pause the previous slide's video, never the one just started.
          this.lastVideo && this.lastVideo !== video && this.lastVideo.pause();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                this.lastVideo = video;
              })
              // Autoplay refused (e.g. iPhone Low Power Mode). AbortError is only a
              // pause interrupting play(), not a refusal.
              .catch((err) => {
                if (!err || err.name !== "AbortError") this.imgFallback(video);
              });
          }
          // Some iPhones in Low Power Mode neither play nor reject, and never buffer either, so don't wait
          // for data there: still paused after 2.5s on WebKit = use the animated-image version. Other
          // browsers keep the old rule (paused with data ready), since they can't show an MP4 as an image.
          setTimeout(() => {
            if (!video.paused || video.dataset.ojImg) return;
            if (this.isWebKit() || video.readyState >= 3) this.imgFallback(video);
          }, 2500);
        }
      } else {
        this.lastVideo && this.lastVideo.pause();
        this.lastVideo = null;
      }
    }

    // iPhone/iPad (every iOS browser is WebKit) or desktop Safari: the browsers that animate an MP4 in <img>.
    isWebKit() {
      const ua = navigator.userAgent;
      return /iP(hone|ad|od)/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) ||
        (/Safari\//.test(ua) && !/Chrome|Chromium|CriOS|Edg|OPR|Android/.test(ua));
    }

    watchLazyVideo(video) {
      if (this.lazyObserver) return;
      const start = () => {
        video.dataset.ojNear = "1";
        video.preload = "auto";
        this.lazyObserver && this.lazyObserver.disconnect();
        this.handleChange(this.slider);
      };
      if (!("IntersectionObserver" in window)) return start();
      this.lazyObserver = new IntersectionObserver(
        (entries) => { if (entries.some((e) => e.isIntersecting)) start(); },
        { rootMargin: "400px 0px" }
      );
      this.lazyObserver.observe(this);
    }

    // iPhone Low Power Mode refuses play() on every video, muted and playsinline
    // included. Safari still animates the same MP4 when it is loaded as an <img>
    // (silent, looping), and Low Power Mode doesn't block images. Browsers that
    // can't show an MP4 as an image fire onerror, so they keep the poster.
    imgFallback(video) {
      if (video.dataset.ojImg) return;
      const src = this.videoSource(video);
      if (!src) return;
      video.dataset.ojImg = "1";
      const img = new Image();
      img.alt = "";
      img.setAttribute("aria-hidden", "true");
      img.className = "m-slide__video-img";
      img.onload = () => {
        if (img.naturalWidth > 0) video.insertAdjacentElement("afterend", img);
      };
      img.src = src;
    }

    // The file the video would play here: its current source, else the first
    // <source> whose media query matches (the phone file comes first).
    videoSource(video) {
      if (video.currentSrc) return video.currentSrc;
      for (const source of video.querySelectorAll("source")) {
        if (!source.media || window.matchMedia(source.media).matches) return source.src;
      }
      return null;
    }

    onBlockSelect(ev) {
      const block = ev.target;
      const index = Number(block.dataset.slide);
      this.lastActive = index;
      if (this.slider) {
        this.slider.slideToLoop(index);
      }
    }
  }

  customElements.define("m-slideshow", MSlideshow);
}
